import { t } from '../../../../../../../services/i18nService';

const WYSIWYG_MARGIN_TOP = 30;

const expandPlugin = {
    register(globalCK) {
        if (globalCK.plugins.get('ia-expand-tool')) {
            return;
        }

        globalCK.plugins.add('ia-expand-tool', {
            icons: '',
            init: (editorInstance) => { this.init(editorInstance); },
        });
    },

    init(editorInstance) {
        editorInstance.addCommand('toggleExpand', {
            exec: () => { this.toggle(editorInstance); },
        });
        editorInstance.ui.addButton('IA Expand', {
            label: this.getExpandLabel(),
            command: 'toggleExpand',
            toolbar: 'expand',
        });

        const resize = () => {
            this.resize(editorInstance);
        };

        editorInstance.on('instanceReady', () => {
            this.setExpandButtonLabel(editorInstance, this.getExpandLabel());
            window.addEventListener('resize', resize);
        });

        editorInstance.on('destroy', () => {
            try {
                this.restoreFooterNavWrapperLayer(editorInstance);
            } catch (error) {
                window.console.error(error);
            } finally {
                this.clearEditorRestoreState(editorInstance);
                window.removeEventListener('resize', resize);
            }
        });
    },

    toggle(editorInstance) {
        if (this.isExpanded(editorInstance)) {
            this.restore(editorInstance);
        } else {
            this.expand(editorInstance);
        }
    },

    expand(editorInstance) {
        let hasRaisedFooterLayer = false;

        try {
            const outerElement = this.getOuterElement(editorInstance);
            const wysiwygElement = this.getWysiwygElement(editorInstance);
            const expandedHeight = this.calculateHeight(editorInstance);

            this.setEditorRestoreState(editorInstance, {
                height: wysiwygElement.style.height,
                marginTop: wysiwygElement.style.marginTop,
                width: outerElement.style.width,
                position: outerElement.style.position,
                top: outerElement.style.top,
                left: outerElement.style.left,
                zIndex: outerElement.style.zIndex,
            });

            this.applyExpandedOuterStyles(editorInstance, outerElement);
            wysiwygElement.style.height = `${expandedHeight}px`;
            wysiwygElement.style.marginTop = `${WYSIWYG_MARGIN_TOP}px`;

            this.raiseFooterNavWrapperLayer(editorInstance);
            hasRaisedFooterLayer = true;

            this.setExpandButtonLabel(editorInstance, this.getCollapseLabel());
        } catch (error) {
            if (hasRaisedFooterLayer) {
                this.restoreFooterNavWrapperLayer(editorInstance);
            }

            const hasRolledBackEditor = this.rollbackExpandEditorStyles(editorInstance);
            if (hasRolledBackEditor) {
                this.clearEditorRestoreState(editorInstance);
            }

            window.console.error(error);
        }
    },

    applyExpandedOuterStyles(editorInstance, outerElement) {
        outerElement.style.width = '100%';
        outerElement.style.position = 'fixed';
        outerElement.style.top = `${this.getTopOffset(editorInstance)}px`;
        outerElement.style.left = '0px';
        outerElement.style.zIndex = '200';
        outerElement.classList.add('ckeditor-full-screen');
    },

    restoreOuterStyles(outerElement, editorRestoreState) {
        if (!outerElement) {
            return;
        }

        if (editorRestoreState.width !== undefined) {
            outerElement.style.width = editorRestoreState.width || '';
        }

        if (editorRestoreState.position !== undefined) {
            outerElement.style.position = editorRestoreState.position || '';
        }

        if (editorRestoreState.top !== undefined) {
            outerElement.style.top = editorRestoreState.top || '';
        }

        if (editorRestoreState.left !== undefined) {
            outerElement.style.left = editorRestoreState.left || '';
        }

        if (editorRestoreState.zIndex !== undefined) {
            outerElement.style.zIndex = editorRestoreState.zIndex || '';
        }

        outerElement.classList.remove('ckeditor-full-screen');
    },

    rollbackExpandEditorStyles(editorInstance) {
        try {
            const editorRestoreState = this.getEditorRestoreState(editorInstance);
            this.restoreOuterStyles(this.getOuterElement(editorInstance), editorRestoreState);

            const wysiwygElement = this.getWysiwygElement(editorInstance);
            if (wysiwygElement) {
                if (editorRestoreState.height !== undefined) {
                    wysiwygElement.style.height = editorRestoreState.height || '';
                }

                if (editorRestoreState.marginTop !== undefined) {
                    wysiwygElement.style.marginTop = editorRestoreState.marginTop || '';
                }
            }

            return true;
        } catch (error) {
            window.console.error(error);
            return false;
        }
    },

    restore(editorInstance) {
        try {
            const editorRestoreState = this.getEditorRestoreState(editorInstance);
            this.restoreOuterStyles(this.getOuterElement(editorInstance), editorRestoreState);

            this.restoreFooterNavWrapperLayer(editorInstance);

            const wysiwygElement = this.getWysiwygElement(editorInstance);
            if (editorRestoreState.height !== undefined) {
                wysiwygElement.style.height = editorRestoreState.height || '';
            }
            if (editorRestoreState.marginTop !== undefined) {
                wysiwygElement.style.marginTop = editorRestoreState.marginTop || '';
            }
            this.clearEditorRestoreState(editorInstance);

            this.setExpandButtonLabel(editorInstance, this.getExpandLabel());
        } catch (error) {
            window.console.error(error);
        }
    },

    resize(editorInstance) {
        try {
            if (this.isExpanded(editorInstance)) {
                const wysiwygElement = this.getWysiwygElement(editorInstance);
                wysiwygElement.style.height = `${this.calculateHeight(editorInstance)}px`;
            }
        } catch (error) {
            window.console.error(error);
        }
    },

    calculateHeight(editorInstance) {
        const winHeight = window.innerHeight;
        const bottomElement = this.getBottomElement(editorInstance);
        const topBarElement = this.getTopBarElement(editorInstance);

        return winHeight
            - this.getTopOffset(editorInstance)
            - this.getFooterOffset()
            - bottomElement.offsetHeight
            - topBarElement.offsetHeight
            - WYSIWYG_MARGIN_TOP;
    },

    getOuterElement(editorInstance) {
        return document.querySelector(`.${editorInstance.id}`);
    },

    getTopBarElement(editorInstance) {
        return document.querySelector(`#${editorInstance.id}_top`);
    },

    getWysiwygElement(editorInstance) {
        return document.querySelector(`.${editorInstance.id} div.cke_wysiwyg_div`);
    },

    getBottomElement(editorInstance) {
        return document.querySelector(`#${editorInstance.id}_bottom`);
    },

    getTopOffset() {
        const headerElement = document.getElementById('header');
        return headerElement ? parseInt(headerElement.offsetHeight, 10) : 0;
    },

    getFooterNavWrapperElement() {
        return document.getElementById('footer-nav-wrapper');
    },

    getEditorRestoreStateStore() {
        if (!this.editorRestoreStateStore) {
            this.editorRestoreStateStore = new WeakMap();
        }

        return this.editorRestoreStateStore;
    },

    setEditorRestoreState(editorInstance, editorRestoreState) {
        this.getEditorRestoreStateStore().set(editorInstance, editorRestoreState);
    },

    getEditorRestoreState(editorInstance) {
        return this.getEditorRestoreStateStore().get(editorInstance) || {};
    },

    clearEditorRestoreState(editorInstance) {
        this.getEditorRestoreStateStore().delete(editorInstance);
    },

    getFooterNavWrapperLayerState() {
        if (!this.footerNavWrapperLayerState) {
            this.footerNavWrapperLayerState = {
                activeEditorCount: 0,
                activeEditors: new WeakSet(),
                originalZIndex: '',
            };
        }

        return this.footerNavWrapperLayerState;
    },

    raiseFooterNavWrapperLayer(editorInstance) {
        const footerLayerState = this.getFooterNavWrapperLayerState();
        if (footerLayerState.activeEditors.has(editorInstance)) {
            return;
        }

        const footerNavWrapperElement = this.getFooterNavWrapperElement();

        if (footerLayerState.activeEditorCount === 0 && footerNavWrapperElement) {
            footerLayerState.originalZIndex = footerNavWrapperElement.style.zIndex;
        }

        footerLayerState.activeEditors.add(editorInstance);
        footerLayerState.activeEditorCount += 1;

        if (footerNavWrapperElement) {
            footerNavWrapperElement.style.zIndex = '201';
        }
    },

    restoreFooterNavWrapperLayer(editorInstance) {
        const footerLayerState = this.getFooterNavWrapperLayerState();
        if (!footerLayerState.activeEditors.has(editorInstance)) {
            return;
        }

        footerLayerState.activeEditors.delete(editorInstance);
        footerLayerState.activeEditorCount = Math.max(0, footerLayerState.activeEditorCount - 1);

        const footerNavWrapperElement = this.getFooterNavWrapperElement();
        if (footerLayerState.activeEditorCount !== 0) {
            if (footerNavWrapperElement) {
                footerNavWrapperElement.style.zIndex = '201';
            }
            return;
        }

        if (footerNavWrapperElement) {
            footerNavWrapperElement.style.zIndex = footerLayerState.originalZIndex;
        }
        footerLayerState.originalZIndex = '';
    },

    getFooterOffset() {
        const footerElement = document.getElementById('playerFooter') || document.querySelector('footer');
        return footerElement ? footerElement.offsetHeight : 0;
    },

    isExpanded(editorInstance) {
        const outerElement = this.getOuterElement(editorInstance);
        return outerElement?.classList.contains('ckeditor-full-screen') || false;
    },

    getExpandButtonLabelElement(editorInstance) {
        return editorInstance.container?.$?.querySelector('.expand_label') || null;
    },

    getExpandButtonElement(buttonLabelElement) {
        if (!buttonLabelElement) {
            return null;
        }

        const closestButtonElement = buttonLabelElement.closest?.('a.cke_button');
        if (closestButtonElement) {
            return closestButtonElement;
        }

        let currentElement = buttonLabelElement.parentElement;
        while (currentElement) {
            if (currentElement.tagName === 'A' && currentElement.classList?.contains('cke_button')) {
                return currentElement;
            }

            currentElement = currentElement.parentElement;
        }

        return null;
    },

    setExpandButtonLabel(editorInstance, label) {
        const buttonLabelElement = this.getExpandButtonLabelElement(editorInstance);
        if (!buttonLabelElement) {
            return;
        }

        buttonLabelElement.textContent = label;
        const buttonElement = this.getExpandButtonElement(buttonLabelElement);

        if (buttonElement) {
            buttonElement.title = label;
            buttonElement.ariaLabel = label;
            if (buttonElement.dataset) {
                buttonElement.dataset.ckeTooltipText = label;
            } else {
                buttonElement.setAttribute('data-cke-tooltip-text', label);
            }
            if (this.isExpanded(editorInstance)) {
                buttonElement.classList.add('cke_button_on');
            } else {
                buttonElement.classList.remove('cke_button_on');
            }
        }
    },

    getExpandLabel() {
        return this.getTranslationOrFallback('EXPAND', 'Expand');
    },

    getCollapseLabel() {
        return this.getTranslationOrFallback('COLLAPSE', 'Collapse');
    },

    getTranslationOrFallback(key, fallbackValue) {
        const translatedLabel = t(key);
        if (translatedLabel === undefined || translatedLabel === null || translatedLabel === '' || translatedLabel === key) {
            return fallbackValue;
        }

        return translatedLabel;
    },
};

export default expandPlugin;
